import dayjs from 'dayjs'
import PortalVue from 'portal-vue'
import Vue from 'vue'
import { library as faLibrary } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome'
import VueI18n from 'vue-i18n'
import TextareaAutosize from 'vue-textarea-autosize'
import Toast, { POSITION } from 'vue-toastification'
import VueVisible from 'vue-visible'
import vClickOutside from 'v-click-outside'

import * as CONSTANTS from '@shared/constants'
import store from './store/store.js'
import * as icons from './icons'
import Loader from '@/components/Loader'
import Checkbox from '@/components/Checkbox'
import Radio from '@/components/Radio'
import Modal from '@/components/Modal'
import Emote from '@/components/emotes/Emote'
import ErrorMessage from '@/components/ErrorMessage'
import LabelWithHelpText from '@/components/LabelWithHelpText'
import * as utils from '@/utils'
import api from './api'

faLibrary.add(icons)

Vue.config.productionTip = false
Vue.config.errorHandler = (err, vm, info) => {
    debug('vue errorHandler', err, vm, info)
    Sentry.captureException(err)
}
Vue.config.warnHandler = (msg, vm, trace) => {
    debug('vue warnHandler', msg, vm, trace)
}

Vue.use(vClickOutside)
Vue.use(PortalVue)
Vue.use(TextareaAutosize)
Vue.use(VueVisible)
Vue.use(VueI18n)

// TODO: The config page will want toasts appended to document.body
// https://github.com/Maronato/vue-toastification/issues/70#issuecomment-618732392
function getToastContainerAsync () {
    return new Promise(resolve => {
        const observer = new MutationObserver(function (mutations, me) {
            const container = document.querySelector('.page-viewer')
            if (container) {
                me.disconnect()
                resolve(container)
            }
        })
        observer.observe(document, {
            childList: true,
            subtree: true
        })
    })
}

Vue.use(Toast, {
    position: POSITION.BOTTOM_CENTER,
    pauseOnFocusLoss: false,
    maxToasts: 5,
    timeout: 4000,
    // Prefer user to click on close button to close, rather than the toast.
    closeOnClick: false,
    toastClassName: 'sbox-toast',
    containerClassName: 'sbox-toast-container',
    transitionDuration: {
        enter: 400,
        leave: 300
    },
    transition: 'Vue-Toastification__fade',
    container: getToastContainerAsync
})

Vue.component('Loader', Loader)
Vue.component('Checkbox', Checkbox)
Vue.component('Radio', Radio)
Vue.component('Modal', Modal)
Vue.component('Emote', Emote)
Vue.component('ErrorMessage', ErrorMessage)
Vue.component('LabelWithHelpText', LabelWithHelpText)
Vue.component('FaIcon', FontAwesomeIcon)
Vue.component('FaLayers', FontAwesomeLayers)

const SI_SYMBOL = ['', 'k', 'M', 'G', 'T', 'P', 'E']
// https://stackoverflow.com/a/40724354/2498782
Vue.filter('bigNumber', num => {
    // what tier? (determines SI symbol)
    const tier = Math.log10(num) / 3 | 0

    // if zero, we don't need a suffix
    if (tier === 0) return num

    // get suffix and determine scale
    const suffix = SI_SYMBOL[tier]
    const scale = Math.pow(10, tier * 3)

    // scale the number
    const scaled = num / scale

    // Displaying "X.0" is pointless, strip trailing ".0"
    const formatted = scaled.toFixed(1).replace(/\.0$/, '')

    return formatted + suffix
})

Vue.filter('relativeTime', date => dayjs(date).from(new Date()))

Vue.directive('focus', {
    inserted: (el, { modifiers }) => {
        if (modifiers.deep) {
            el.querySelector('input, textarea')?.focus()
        } else {
            el.focus()
        }
    },
})

const requestIdListener = event => {
    event.stopPropagation()
    store.dispatch('requestIdShare')
}
Vue.directive('require-identity', el => {
    // Remove UI restrictions if user grants ID.
    if (store.getters.isIdentityLinkedUser) {
        el.style.cursor = null
        el.removeEventListener('click', requestIdListener, { capture: true })
    } else {
        el.style.cursor = 'not-allowed'
        // Stop clicks but allow hover, so the title can appear.
        el.addEventListener('click', requestIdListener, { capture: true })
    }
})

Vue.prototype.$cl = console.log
Vue.prototype.$api = api
Vue.prototype.$emoteToHTML = utils.emoteToHTML
Vue.prototype.$twemojiToUrl = utils.twemojiToUrl

// https://vuejs.org/v2/cookbook/adding-instance-properties.html#Base-Example
// Make env variables available in all vue components
// Reference a variable in a template like so: {{ $env.<VAR_NAME> }}
Vue.prototype.$const = Object.freeze(CONSTANTS)
Vue.prototype.$env = Object.freeze(process.env)
